/**
 * MOCK/TEMPORARY — authentication.
 *
 * There is NO real authentication. Any email that looks valid with a password
 * of 6+ characters "succeeds", and the returned user is derived from the email
 * on the client. This mirrors exactly what the prototype did inline; it is
 * preserved rather than removed (Phase 2 isolates simulations, it does not
 * replace them).
 *
 * ---------------------------------------------------------------------------
 * WHAT IS A MOCK HERE AND WHAT IS AN HONEST REFUSAL — the two are not the same, and
 * §32 warns against reading one as the other ("Do not claim an API is implemented
 * merely because an interface exists").
 *
 *   RESOLVE WITH A FIXTURE — login, signup, loginAsDemo, logout, updateProfile,
 *     requestAccountDeletion. Each pretends to succeed, and each is a simulation the
 *     current UX depends on. Phase 2 isolated these; it did not remove them.
 *   RESOLVE TRUTHFULLY — getSession, which answers "nobody is signed in" because that
 *     is the case. It is the only read here that needs no pretence.
 *   REFUSE — signInWithProvider, refreshSession, requestPasswordReset,
 *     confirmPasswordReset, verifyEmail. All 501. Every one of them would have to
 *     invent a token, a mail sender or a third-party identity to resolve, and a
 *     resolved refusal is indistinguishable from a working feature.
 * ---------------------------------------------------------------------------
 *
 * Real endpoints: docs/api-contract.md, "Authentication".
 */
import { OAUTH_PROVIDERS, USE_MOCK_API } from '../config';
import { newId } from '../lib/ids';
import type {
  AppAccess,
  AuthProviderConfig,
  AuthProviderId,
  AuthSession,
  LoginPayload,
  SignupPayload,
  User,
} from '../types/domain';
import { ApiError } from './errors';
import { http } from './http';
import { assertMock, mockDelay } from './mock';

/**
 * The demo account used by the "Skip — use the demo account" button.
 *
 * ============================================================================
 * THE ONE HARDCODED IDENTITY IN THE APP, AND IT IS REACHED ONLY BY A BUTTON THAT SAYS
 * WHAT IT IS. Batch 7 §5: "The frontend must not permanently depend on hardcoded/mock
 * user identity." It does not — `login` and `signup` derive their user from what was
 * typed, `getSession` below returns null rather than this, and nothing outside
 * `loginAsDemo` reads it. It is the fixture behind ONE explicitly-labelled entry point,
 * which is a different thing from an identity the app assumes.
 *
 * The dependency that DID exist was in `authStore.selectProfileName`, which substituted
 * this name for any signed-out read — so a header could render "Goodluck Efe" with no
 * session at all. That is gone; see the note on that selector.
 *
 * §20.7 of docs/api-contract.md lists the demo bypass as something the backend must not
 * inherit: it either goes, or it becomes a real provisioned account with real limits.
 * ============================================================================
 */
export const DEMO_USER: User = {
  id: 'demo-user',
  name: 'Goodluck Efe',
  email: 'goodluck.efe@example.com',
  authProvider: 'password',
  emailVerifiedAt: '2026-01-01T00:00:00.000Z',
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Deterministic verified-at instant for the client-side auth fixtures. */
const MOCK_EMAIL_VERIFIED_AT = '2026-01-01T00:00:00.000Z';

/**
 * The one Google-owned account fixture the mock reset flow recognises.
 *
 * The mock has no account store, so it cannot truly look an email up and discover which
 * provider owns it. This single synthetic address is the minimum fixture that lets the UI
 * exercise the "use Google instead" branch end to end until a backend owns that lookup.
 */
export const MOCK_GOOGLE_ONLY_ACCOUNT_EMAIL = 'google-only@example.com';

/** What the reset flow says when an account has no password because Google owns it. */
export const GOOGLE_SIGN_IN_RESET_MESSAGE =
  'This account uses Google sign-in. Sign in with Google instead — there is no password to reset.';

/** What the settings flow says when a signed-in Google account reaches password change. */
export const GOOGLE_SIGN_IN_CHANGE_PASSWORD_MESSAGE =
  'This account uses Google sign-in. Sign in with Google instead — there is no password to change.';

/** What the reset page says when the token in the link is unusable. */
export const INVALID_RESET_TOKEN_MESSAGE = 'This reset link is not valid or has expired';

/** Validates an email address for the auth form. Also used by the UI directly. */
function isValidEmail(email: string): boolean {
  return EMAIL_RE.test(email.trim());
}

/**
 * Longest display name the account accepts.
 *
 * A limit rather than none, because a name is rendered in a 26px-tall sidebar row, a
 * fixed-width mini player and an agreement PDF's signature block — all of which ellipsise,
 * so a 400-character name is a name nobody can read anywhere. 60 is generous for a human
 * name and short enough that the header still shows most of it.
 */
export const MAX_NAME_LENGTH = 60;

/**
 * Why a display name is not acceptable, or null when it is.
 *
 * EXPORTED, unlike `isValidEmail`, and deliberately: the Settings form has to disable Save
 * and say why BEFORE a request is made, and the API has to refuse a bad name regardless of
 * what any form did. Two copies of the rule is how those two answers drift apart — so there
 * is one rule and both callers ask it.
 *
 * RETURNS THE MESSAGE rather than a boolean, because "invalid" is not useful copy. The
 * caller shows the string; nothing has to map a false onto an explanation.
 */
export function nameError(name: string): string | null {
  const trimmed = name.trim();
  if (!trimmed) return 'Enter your name';
  if (trimmed.length > MAX_NAME_LENGTH) {
    return `Use ${MAX_NAME_LENGTH} characters or fewer`;
  }
  return null;
}

/**
 * Turns an email local-part into a display name: "goodluck.efe" -> "Goodluck Efe".
 * Client-side only — a real backend stores the name the user signed up with.
 */
function deriveNameFromEmail(email: string): string {
  return email
    .split('@')[0]
    .replace(/[._]+/g, ' ')
    .replace(/\b\w/g, (ch) => ch.toUpperCase());
}

/** Session shape returned by every successful auth call. */
function sessionFor(user: User, appAccess: AppAccess = 'granted'): AuthSession {
  // token stays null: there is no server to issue one, and a fake token would
  // invite code elsewhere to start treating it as real.
  return { user, isAuthenticated: true, token: null, appAccess, checkoutOwnership: {} };
}

/**
 * Looks a provider's configuration up, or null if it is not one we offer.
 *
 * Reads config.OAUTH_PROVIDERS rather than taking a client id as an argument, so
 * there is one answer to "is Google set up" and the buttons, the API and any future
 * callback route all get it from the same place.
 */
export function providerConfig(provider: AuthProviderId): AuthProviderConfig | null {
  return OAUTH_PROVIDERS.find((p) => p.id === provider) ?? null;
}

/**
 * What the UI is told when a provider button is pressed in this build.
 *
 * Exported so the auth screen and its test name the same sentence, and so the
 * wording — which is a promise to the user that nothing happened — is in one place.
 */
export const PROVIDER_UNCONFIGURED_MESSAGE =
  'Google and Apple sign-in are not configured yet — use your email and password for now';

export const authApi = {
  /** MOCK: POST /auth/login */
  async login(payload: LoginPayload): Promise<AuthSession> {
    if (!USE_MOCK_API) {
      return http.post<AuthSession>('/auth/login', payload, {
        auth: false,
        credentials: 'include',
      });
    }
    assertMock('authApi.login');
    if (!isValidEmail(payload.email)) {
      throw new ApiError(422, 'Enter a valid email address');
    }
    if (payload.password.length < 6) {
      throw new ApiError(422, 'Password must be at least 6 characters');
    }
    const email = payload.email.trim();
    const user: User = {
      id: newId(),
      name: deriveNameFromEmail(email) || DEMO_USER.name,
      email,
      authProvider: 'password',
      emailVerifiedAt: MOCK_EMAIL_VERIFIED_AT,
    };
    return mockDelay(sessionFor(user));
  },

  /** MOCK: POST /auth/signup */
  async signup(payload: SignupPayload): Promise<AuthSession> {
    if (!USE_MOCK_API) {
      return http.post<AuthSession>('/auth/signup', payload, {
        auth: false,
        credentials: 'include',
      });
    }
    assertMock('authApi.signup');
    if (!isValidEmail(payload.email)) {
      throw new ApiError(422, 'Enter a valid email address');
    }
    if (payload.password.length < 10) {
      throw new ApiError(422, 'Password must be at least 10 characters');
    }
    if (!payload.name.trim()) {
      throw new ApiError(422, 'Enter your name');
    }
    const user: User = {
      id: newId(),
      name: payload.name.trim(),
      email: payload.email.trim(),
      authProvider: 'password',
      // Signup now enters the verification flow first. In the mock build the route is still
      // simulated client-side, but the session state matches the real shape: the account
      // exists and the address is not verified yet.
      emailVerifiedAt: null,
    };
    return mockDelay({ user, isAuthenticated: false, token: null, appAccess: 'purchase_required' });
  },

  /**
   * Starts the real Google flow through the backend, which owns state, PKCE and the
   * redirect URI. Mock mode still refuses because it has no identity provider.
   */
  async signInWithProvider(provider: AuthProviderId): Promise<AuthSession> {
    const config = providerConfig(provider);
    if (!config) {
      throw new ApiError(400, `Unknown sign-in provider "${provider}"`);
    }
    if (!config.configured) {
      throw new ApiError(
        501,
        `${config.label} sign-in is not configured. Set VITE_${provider.toUpperCase()}_CLIENT_ID and the matching server credentials (see .env.example).`
      );
    }

    if (!USE_MOCK_API) {
      if (provider !== 'google') {
        throw new ApiError(
          501,
          `${config.label} sign-in is not configured yet.`,
          'unsupported_capability'
        );
      }
      const { authorizeUrl } = await http.post<{ authorizeUrl: string }>(
        '/auth/oauth/google/start',
        {},
        { auth: false, credentials: 'include' }
      );
      if (typeof window !== 'undefined') window.location.assign(authorizeUrl);
      return new Promise<AuthSession>(() => {});
    }

    assertMock('authApi.signInWithProvider');
    throw new ApiError(
      501,
      `${config.label} sign-in cannot complete: the authorization code has to be exchanged for a session by the backend, and no backend is configured (VITE_API_BASE_URL is empty).`
    );
  },

  async completeProviderSignIn(input: {
    provider: AuthProviderId;
    code: string;
    state: string;
  }): Promise<AuthSession> {
    if (!USE_MOCK_API) {
      return http.post<AuthSession>(
        `/auth/oauth/${input.provider}`,
        {
          code: input.code,
          state: input.state,
        },
        { auth: false, credentials: 'include' }
      );
    }
    throw new ApiError(
      501,
      'Google sign-in cannot complete without a backend OAuth exchange.',
      'unsupported_capability'
    );
  },

  /** MOCK: no request — the demo account short-circuits auth entirely. */
  async loginAsDemo(): Promise<AuthSession> {
    if (!USE_MOCK_API) {
      throw new ApiError(
        501,
        'The demo account is not available once a backend is configured.',
        'unsupported_capability'
      );
    }
    assertMock('authApi.loginAsDemo');
    return mockDelay(sessionFor(DEMO_USER));
  },

  /** MOCK: POST /auth/logout */
  async logout(): Promise<void> {
    if (!USE_MOCK_API) {
      await http.post<void>('/auth/logout', undefined, { credentials: 'include' });
      return;
    }
    assertMock('authApi.logout');
    return mockDelay(undefined);
  },

  /* =========================================================================
     SESSION LIFECYCLE — restoration, refresh and recovery (Batch 7 §5)
     =========================================================================
     §5 asks for eleven things. Six were already here: registration, login, logout,
     profile get/update, and auth errors (every refusal above is an `ApiError` with a
     status the §21 contract maps — see api/errors.ts). The five below are the rest:
     current user/session, session restoration, expired sessions, password reset and
     email verification.

     WHAT MAKES THESE SEAMS RATHER THAN FEATURES, and the distinction the brief draws in
     the same paragraph: "Do not build the actual authentication backend." A password
     reset is not a frontend feature at all — it is a token generator, a mail sender and
     a single-use redemption record, none of which can exist in a browser. What CAN exist
     is the exact call the screen will make, refusing honestly until there is something
     behind it, so that building the screen is a screen's work and not a redesign.

     WHY THEY REFUSE INSTEAD OF RESOLVING. `resetPassword` returning `undefined` would be
     the worst available answer: the standard implementation of that endpoint is
     deliberately indistinguishable from success whether or not the email exists, so a
     mock that resolves is indistinguishable from a working feature. A user would be told
     to check their inbox and no email would ever arrive. `signInWithProvider` above
     already made this call for OAuth, for the same reason and with the same 501.

     `getSession` is the exception and does NOT throw — see its own note.

     Contract: docs/api-contract.md §4 "Not yet designed" is where these were recorded as
     absent; §4 and §5 now carry the shapes.
     ========================================================================= */

  /**
   * MOCK: GET /auth/session — who is signed in, according to the server.
   *
   * ============================================================================
   * THE ONLY ONE OF THESE THAT RESOLVES, AND IT RESOLVES TO "NOBODY". That is not a
   * refusal dressed as an answer: "is there a session?" is a question this build can
   * answer truthfully, and the truthful answer is no. There is no token, nothing is
   * persisted, and a reload signs you out — which `authStore.restoreSession` then
   * reports as a completed check with no user rather than as an error.
   *
   * A THROW HERE WOULD BE WRONG in a way the others are not. This is the first call on
   * app load; an app whose boot sequence fails on every start would have to special-case
   * the failure, and that special case is where "treat an error as signed out" gets
   * written — which is exactly the code that would later swallow a real outage and log
   * everybody out silently.
   * ============================================================================
   *
   * The real endpoint reads the bearer token, returns the account it belongs to, and
   * answers `401` when it does not belong to one — which the client turns back into this
   * same null session. See §21's `isUnauthorized`.
   */
  async getSession(): Promise<AuthSession> {
    if (!USE_MOCK_API) {
      return http.get<AuthSession>('/auth/session', { auth: false, credentials: 'include' });
    }
    assertMock('authApi.getSession');
    // Not `sessionFor(...)`: that helper asserts an authenticated session, and there
    // is none. The empty session is the honest fixture.
    return mockDelay({
      user: null,
      isAuthenticated: false,
      token: null,
      appAccess: 'purchase_required',
    });
  },

  /**
   * NOT IMPLEMENTED: POST /auth/refresh — exchange a refresh token for a new session.
   *
   * ALWAYS THROWS. There is no token to refresh: `AuthSession.token` is null on every
   * session this module issues, and `expiresAt` is absent, so there is not even an
   * expiry to pre-empt. A mock that returned a fresh session would be inventing the one
   * thing refresh exists to manage.
   *
   * WHY THE SEAM IS WORTH HAVING ANYWAY. Refresh is the piece that decides where expiry
   * is handled, and that decision shapes code far from here: either every caller catches
   * a 401 and retries, or one place does it and nothing else knows tokens expire. The
   * second is right, and it wants a single function to call — this one. The API client
   * that does the retry does not exist yet either (there are no real requests to wrap),
   * which is why this is a boundary and not a bug.
   */
  async refreshSession(): Promise<AuthSession> {
    if (!USE_MOCK_API) {
      throw new ApiError(501, 'Sessions cannot be refreshed yet.', 'unsupported_capability');
    }
    assertMock('authApi.refreshSession');
    throw new ApiError(
      501,
      'Sessions cannot be refreshed: this build issues no tokens, so there is nothing to exchange. Requires a backend that issues access and refresh tokens.',
      'unsupported_capability'
    );
  },

  /**
   * NOT IMPLEMENTED: POST /auth/password/reset — request a reset email.
   *
   * ============================================================================
   * ALWAYS THROWS, AND RESOLVING WOULD BE A LIE WITH A UI ATTACHED. The real endpoint
   * answers 204 whether or not the address has an account — that is deliberate, so it
   * cannot be used to enumerate users — which means a mock that resolves produces the
   * exact same observable behaviour as a working reset: "Check your email." Nothing
   * would arrive, and nothing on screen would say why.
   * ============================================================================
   *
   * The email is validated first so the refusal is about the missing backend rather than
   * about a typo, and so this behaves like every other call in the file: a 422 for a bad
   * request, a 501 for a capability that does not exist.
   *
   * NO SCREEN CALLS THIS. There is no "Forgot password?" link in the app — see
   * routes/Login.tsx — and adding one that led to a permanent refusal would be worse
   * than not offering it. This is the call that link will make.
   *
   * What the backend needs: a single-use, expiring, cryptographically random token bound
   * to the account (never derived from the email), a mail sender, a redemption endpoint
   * that invalidates every existing session on success, and rate limiting per address
   * and per IP.
   */
  async requestPasswordReset(email: string, turnstileToken: string): Promise<void> {
    if (!isValidEmail(email)) {
      throw new ApiError(422, 'Enter a valid email address');
    }
    const normalizedEmail = email.trim().toLowerCase();
    if (!USE_MOCK_API) {
      await http.post<void>('/auth/password/reset', { email, turnstileToken }, { auth: false });
      return;
    }
    assertMock('authApi.requestPasswordReset');
    if (normalizedEmail === MOCK_GOOGLE_ONLY_ACCOUNT_EMAIL) {
      throw new ApiError(422, GOOGLE_SIGN_IN_RESET_MESSAGE, 'oauth_passwordless_account');
    }
    throw new ApiError(
      501,
      'Password reset is not available: there is no backend to issue a reset token and no mail sender to deliver it. Nothing was sent.',
      'unsupported_capability'
    );
  },

  /**
   * NOT IMPLEMENTED: POST /auth/password/reset/confirm — redeem a reset token.
   *
   * The other half of the flow above, and it throws for the same reason: a token this
   * build cannot have issued is a token it cannot verify. Accepting one would mean
   * accepting any string, which is a password change with no authentication in front
   * of it.
   *
   * Validates the new password against the same 6-character rule `login` and `signup`
   * apply, so the rule has one home. §32/§20.8: that check is UX, not a control — the
   * server must apply its own.
   */
  async validatePasswordResetToken(token: string): Promise<void> {
    if (!token.trim()) {
      throw new ApiError(422, INVALID_RESET_TOKEN_MESSAGE, 'invalid_reset_token');
    }
    if (!USE_MOCK_API) {
      await http.post<void>('/auth/password/reset/validate', { token }, { auth: false });
      return;
    }
    assertMock('authApi.validatePasswordResetToken');
    throw new ApiError(
      501,
      'Password reset links cannot be checked in mock mode because no backend issued them.',
      'unsupported_capability'
    );
  },

  async confirmPasswordReset(token: string, newPassword: string): Promise<void> {
    if (!token.trim()) {
      throw new ApiError(422, 'This reset link is not valid');
    }
    if (newPassword.length < 10) {
      throw new ApiError(422, 'Password must be at least 10 characters');
    }
    if (!USE_MOCK_API) {
      await http.post<void>(
        '/auth/password/reset/confirm',
        { token, newPassword },
        { auth: false }
      );
      return;
    }
    assertMock('authApi.confirmPasswordReset');
    throw new ApiError(
      501,
      'Password reset cannot be completed: no backend issued this token, so nothing can verify it. The password was not changed.',
      'unsupported_capability'
    );
  },

  /**
   * POST /auth/email/verify — redeem an email-verification token.
   *
   * In backend mode this is a real public seam: the token is posted to the server, which
   * decides whether it is valid and which account it proves ownership of.
   *
   * In mock mode it still refuses honestly. There is no mail sender, no issued token and
   * therefore nothing to verify; resolving there would claim a verification email had been
   * sent and redeemed when neither thing happened.
   *
   * The open question this leaves, recorded in docs/api-contract.md §5: whether changing
   * an email through `PATCH /users/me` requires re-verification. The Settings form
   * currently treats the change as immediate, which is a decision the backend can
   * overturn — and if it does, this call is where the resulting flow lands.
   */
  async verifyEmail(token: string, newPassword: string): Promise<AuthSession> {
    if (!token.trim()) {
      throw new ApiError(422, 'This verification link is not valid');
    }
    if (!USE_MOCK_API) {
      return http.post<AuthSession>(
        '/auth/email/verify',
        { token, newPassword },
        { auth: false, credentials: 'include' }
      );
    }
    assertMock('authApi.verifyEmail');
    throw new ApiError(
      501,
      'Email verification is not available: no backend issued this token, and no mail sender delivered a verification link to redeem.',
      'unsupported_capability'
    );
  },

  async resendVerificationEmail(email: string, turnstileToken: string): Promise<void> {
    if (!USE_MOCK_API) {
      await http.post<void>(
        '/auth/email/verify/resend',
        { email, turnstileToken },
        { auth: false, credentials: 'include' }
      );
      return;
    }
    assertMock('authApi.resendVerificationEmail');
    throw new ApiError(
      501,
      'Verification email resend is not available: no backend is configured to issue and send a fresh verification link.',
      'unsupported_capability'
    );
  },

  /**
   * MOCK: PATCH /users/me
   *
   * `avatarUrl` joined `name` when the profile picture landed, rather
   * than getting an endpoint of its own — the picture is a field on the account, and
   * a second profile-writing path would be a second place the account can be updated
   * from. A real implementation splits the *upload* out (a multipart POST returning a
   * URL) but still PATCHes the resulting URL through here.
   *
   * NOTHING IS PERSISTED. The value handed back is the value passed in, so the store
   * ends up holding the data URL the browser read locally. A reload loses it, exactly
   * as it loses the simulated session. The UI states this where the user can see it
   * rather than implying the image was uploaded somewhere.
   *
   * THE NAME IS TRIMMED AND VALIDATED HERE, not only in the form that sends it (Batch 6 §4).
   * A validation that lives only in a component is a validation the next caller does not
   * have — and the stored name is what the sidebar, the mini player and the licence
   * agreements all render, so a whitespace-only name would empty three surfaces at once.
   * 422 for the same reason the email refusal uses it: the request was understood and
   * rejected, which is a different thing from the 501s this file throws for missing backends.
   */
  async updateProfile(patch: Partial<Pick<User, 'name' | 'avatarUrl'>>): Promise<Partial<User>> {
    if (!USE_MOCK_API) {
      return http.patch<Partial<User>>('/users/me', patch);
    }
    assertMock('authApi.updateProfile');
    if (patch.name !== undefined) {
      const problem = nameError(patch.name);
      if (problem) throw new ApiError(422, problem);
      // Trimmed on the way through, so the store never holds a name with edge whitespace
      // that every surface then renders. The caller gets the applied value back.
      return mockDelay({ ...patch, name: patch.name.trim() });
    }
    return mockDelay(patch);
  },

  async changePassword(input: { currentPassword: string; newPassword: string }): Promise<void> {
    if (!USE_MOCK_API) {
      await http.post<void>('/users/me/password', input);
      return;
    }
    assertMock('authApi.changePassword');
    if (!input.currentPassword.trim()) {
      throw new ApiError(422, 'Enter your current password');
    }
    if (input.newPassword.length < 10) {
      throw new ApiError(422, 'Password must be at least 10 characters');
    }
    return mockDelay(undefined);
  },

  /**
   * MOCK: DELETE /users/me
   *
   * Deliberately does not delete anything. The real flow emails a confirmation
   * link, and the UI already says so.
   */
  async requestAccountDeletion(reason: string, detail: string): Promise<void> {
    if (!USE_MOCK_API) {
      await http.request<void>('/users/me', {
        method: 'DELETE',
        body: { reason, detail },
      });
      return;
    }
    assertMock('authApi.requestAccountDeletion');
    return mockDelay(undefined);
  },
};
